import { Injectable, inject } from '@angular/core';
import { Router } from '@angular/router';
import { AppData } from '@shared/models/appdata.model';
import { Navigate } from '@ngxs/router-plugin';
import { Store } from '@ngxs/store';
import { Observable } from 'rxjs';
import { GetSellersAction, ResetDealerAction } from '../states/dealer/dealer.actions';
import { DealerStateSelectors } from '../states/dealer/dealer-state.selectors';
import { diegoRoutes } from '../enums/orderflow-paths';
import { StateHelperService } from './state-helper.service';
import { SetIsMenuOpenAction } from '../states/view/view.actions';
import { GetEmergencyAlertResponse, SetEmergencyAlertResponse } from '@sales/states/sale/sale.actions';
import { AppInsightsService } from './application-insights.service';
import { UserInfoReponse } from '../models/claim.model';
import { HttpClient } from '@angular/common/http';
import { environment } from '@env/environment';
import { SetSalesPersonId } from '../states/user/user.actions';
import { ApiGetOfferingsAction } from '@app/sales/states/offering/offering.actions';
import { UserSelectors } from '../states/user/user.selectors';

enum Messages {
  MissingDiegoAffiliation = 'Du mangler dealer med Diego-tilhørighet',
}

// https://nice-hill-002425310.azurestaticapps.net/docs/intro
@Injectable({
  providedIn: 'root',
})
export class AuthService {
  private stateHelperService = inject(StateHelperService);
  private store = inject(Store);
  private router = inject(Router);
  appData = inject(AppData);
  private appInsights = inject(AppInsightsService);
  private http = inject(HttpClient);

  authErrorMessage: string = '';

  getUserInfo(): Observable<UserInfoReponse> {
    return this.http.get<UserInfoReponse>(this.appData.apiUrl + '/api/v1/account/user-info');
  }

  login(): void {
    this.authErrorMessage = '';

    const loginUrl = environment.proxyApiUrl || this.appData.apiUrl;
    const returnUrl = new URL(window.location.toString()).searchParams.get('returnUrl') || diegoRoutes.startSale;
    const encodedRelativeReturnUrl = encodeURIComponent(returnUrl.replace(this.appData.baseUrl, ''));

    window.location.href = `${loginUrl}/api/v1/account/login?returnUrl=${encodedRelativeReturnUrl}`;
  }

  public checkAuth(): boolean {
    const isAuthenticated = this.store.selectSnapshot(UserSelectors.isAuthenticated);

    if (isAuthenticated) {
      if (this.hasDealerWithDiegoAffiliation()) {
        this.initializeAuthorizedUser();
        return true;
      } else {
        this.resetAndRedirect(Messages.MissingDiegoAffiliation);
        return false;
      }
    }

    this.router.navigate([diegoRoutes.login], { queryParams: { returnUrl: window.location.href } });
    return false;
  }

  private initializeAuthorizedUser(): void {
    this.store.dispatch(new SetSalesPersonId());
    this.store.dispatch(new ApiGetOfferingsAction());
    this.store.dispatch(new GetSellersAction());
    this.store.dispatch(new GetEmergencyAlertResponse());
  }

  private resetAndRedirect(message: string): void {
    this.stateHelperService.resetSaleState();
    this.authErrorMessage = message;
    this.store.dispatch([new ResetDealerAction(), new Navigate([diegoRoutes.login])]);
    this.appInsights.clearAuthenticatedUserContext();
  }

  private hasDealerWithDiegoAffiliation(): boolean {
    const dealers = this.store.selectSnapshot(DealerStateSelectors.dealers);
    return dealers && dealers.length > 0;
  }

  public logoff(): void {
    this.stateHelperService.resetSaleState();
    this.store.dispatch([new ResetDealerAction(), new SetIsMenuOpenAction(false), new SetEmergencyAlertResponse(null)]);
    this.appInsights.clearAuthenticatedUserContext();

    window.location.href = `${this.appData.apiUrl}/api/v1/account/logout`;
  }
}
